import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Calendar, MapPin, Plus, Users, XCircle, Send, MessageSquare, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademySessions({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamIdFilter = searchParams.get("team_id");

  const [sessions, setSessions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [termFilter, setTermFilter] = useState("all");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [periods, setPeriods] = useState([]);
  const [year, setYear] = useState(null);
  const [periodId, setPeriodId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Create session form
  const [selectedTeam, setSelectedTeam] = useState(teamIdFilter || "");
  const [sessionDate, setSessionDate] = useState("");
  const [location, setLocation] = useState("Dempsey");
  const [sessionType, setSessionType] = useState("training");
  const [opponent, setOpponent] = useState("");
  const [reportingTime, setReportingTime] = useState("");
  const [gameTime, setGameTime] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:30");
  const [squadLimit, setSquadLimit] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Edit session state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editSquadLimit, setEditSquadLimit] = useState("");
  const [updating, setUpdating] = useState(false);
  
  // Manage players state
  const [showManagePlayersDialog, setShowManagePlayersDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [managePlayersTab, setManagePlayersTab] = useState("registered"); // "registered" or "guest"
  const [guestPlayerName, setGuestPlayerName] = useState("");
  const [guestPlayerPhone, setGuestPlayerPhone] = useState("");
  const [guestPlayerEmail, setGuestPlayerEmail] = useState("");
  const [addingGuest, setAddingGuest] = useState(false);

  // Cancel session state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingSession, setCancellingSession] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNotifyWhatsApp, setCancelNotifyWhatsApp] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Broadcast message state
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastTeamId, setBroadcastTeamId] = useState("");
  const [broadcastTeams, setBroadcastTeams] = useState([]);
  const [broadcastScope, setBroadcastScope] = useState("assigned");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  const isCoach = user?.roles?.includes("academy_coach");
  const isPlayer = user?.roles?.includes("academy_player");
  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    fetchSessions();
    fetchTeams();
    fetchTerms();
    fetchPeriods();
  }, [teamIdFilter, termFilter]);

  const fetchSessions = async () => {
    try {
      const params = new URLSearchParams();
      if (teamIdFilter) params.append("team_id", teamIdFilter);
      if (termFilter && termFilter !== "all") params.append("term_id", termFilter);
      // Everything, always: the term tabs decide what's on screen.
      params.append("include_past", "true");
      const qs = params.toString();
      const url = `${API}/academy/sessions${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url);
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      const r = await axios.get(`${API}/academy/attendance-hub/calendar`,
        { withCredentials: true });
      const years = r.data.years || [];
      setPeriods(years);
      if (years.length) {
        // Remember where you were, so coming back from an edit doesn't reset it.
        const savedYear = localStorage.getItem("sessionsYear");
        const savedTerm = localStorage.getItem("sessionsTerm");
        const useYear = years.find((y) => y.year === savedYear) || years[0];
        const useTerm = useYear.terms.find((t) => t.term_id === savedTerm)
          || useYear.terms.find((t) => t.marked < t.sessions)
          || useYear.terms[0];
        setYear(useYear.year);
        setPeriodId(useTerm?.term_id || null);
      }
    } catch (error) {
      console.error("Error fetching terms calendar:", error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`);
      // Only offer non-hidden/non-archived terms in the selector.
      const visible = (response.data || []).filter(
        (t) => !t.is_hidden && !t.is_archived && t.status !== "archived"
      );
      setTerms(visible);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/academy/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
    try {
      const r = await axios.get(`${API}/academy/coach/broadcast-teams`, { withCredentials: true });
      setBroadcastTeams(r.data.teams || []);
      setBroadcastScope(r.data.scope || "assigned");
    } catch (error) {
      console.error("Error fetching broadcast teams:", error);
    }
  };

  const createSession = async (e) => {
    e.preventDefault();
    
    if (!selectedTeam || !sessionDate || !location) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      // Combine date and time for session_date
      const sessionDateTime = sessionType === "training" 
        ? `${sessionDate}T${startTime}:00`
        : `${sessionDate}T10:00:00`; // Default time for games
      
      await axios.post(
        `${API}/academy/sessions`,
        {
          team_id: selectedTeam,
          session_date: sessionDateTime,
          location,
          session_type: sessionType,
          start_time: sessionType === "training" ? startTime : null,
          end_time: sessionType === "training" ? endTime : null,
          duration_minutes: sessionType === "training" ? null : 90,
          opponent: sessionType === "game" ? opponent : null,
          reporting_time: sessionType === "game" ? reportingTime : null,
          game_time: sessionType === "game" ? gameTime : null,
          squad_limit: squadLimit ? parseInt(squadLimit) : null,
          standby_promotion: "manual"
        },
        { withCredentials: true }
      );

      toast.success("Session created successfully");
      setShowCreateDialog(false);
      fetchSessions();
      
      // Reset form
      setSessionDate("");
      setOpponent("");
      setReportingTime("");
      setGameTime("");
      setSquadLimit("");
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error(error.response?.data?.detail || "Failed to create session");
    } finally {
      setCreating(false);
    }
  };

  const signupForSession = async (sessionId) => {
    try {
      await axios.post(
        `${API}/academy/sessions/${sessionId}/signup`,
        {},
        { withCredentials: true }
      );
      toast.success("Signed up successfully!");
      fetchSessions();
    } catch (error) {
      console.error("Error signing up:", error);
      toast.error(error.response?.data?.detail || "Failed to sign up");
    }
  };

  const deleteSession = async (session) => {
    const when = new Date(session.session_date).toLocaleDateString();
    const label = session.session_type === "game" ? "game" : "session";
    if (!window.confirm(
      `Delete this ${label} for ${session.team_name} on ${when}?\n\n` +
      "Use this only for one created by mistake — it disappears for everyone. " +
      "If it was a real session that is now off, use Cancel Session instead so parents are told."
    )) return;
    try {
      await axios.delete(`${API}/academy/sessions/${session.id}`, { withCredentials: true });
      toast.success(`${label === "game" ? "Game" : "Session"} deleted`);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete it");
    }
  };

  const openEditDialog = (session) => {    setEditingSession(session);
    const sessionDate = new Date(session.session_date);
    setEditDate(sessionDate.toISOString().split('T')[0]);
    setEditStartTime(session.start_time || "09:00");
    setEditEndTime(session.end_time || "10:30");
    setEditLocation(session.location);
    setEditSquadLimit(session.squad_limit || "");
    setShowEditDialog(true);
  };

  const updateSession = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const sessionDateTime = `${editDate}T${editStartTime}:00`;
      
      await axios.put(
        `${API}/academy/sessions/${editingSession.id}`,
        {
          session_date: sessionDateTime,
          start_time: editStartTime,
          end_time: editEndTime,
          location: editLocation,
          squad_limit: editSquadLimit ? parseInt(editSquadLimit) : null
        },
        { withCredentials: true }
      );

      toast.success("Session updated successfully");
      setShowEditDialog(false);
      fetchSessions();
    } catch (error) {
      console.error("Error updating session:", error);
      toast.error(error.response?.data?.detail || "Failed to update session");
    } finally {
      setUpdating(false);
    }
  };

  const openManagePlayers = async (session) => {
    setSelectedSession(session);
    setShowManagePlayersDialog(true);
    setLoadingPlayers(true);
    
    try {
      // Fetch all players from the team
      const response = await axios.get(`${API}/academy/teams/${session.team_id}`, { withCredentials: true });
      setTeamPlayers(response.data.players || []);
    } catch (error) {
      console.error("Error fetching team players:", error);
      toast.error("Failed to load team players");
    } finally {
      setLoadingPlayers(false);
    }
  };

  const addPlayerToSession = async (playerId) => {
    try {
      await axios.post(
        `${API}/academy/sessions/${selectedSession.id}/add-player`,
        { player_id: playerId },
        { withCredentials: true }
      );
      toast.success("Player added to session");
      fetchSessions();
      
      // Refresh selected session
      const updatedSessions = sessions.map(s => 
        s.id === selectedSession.id 
          ? {...s, signups: [...(s.signups || []), playerId]}
          : s
      );
      const updated = updatedSessions.find(s => s.id === selectedSession.id);
      setSelectedSession(updated);
    } catch (error) {
      console.error("Error adding player:", error);
      toast.error(error.response?.data?.detail || "Failed to add player");
    }
  };

  const addGuestPlayerToSession = async () => {
    if (!guestPlayerName.trim()) {
      toast.error("Please enter guest player name");
      return;
    }

    setAddingGuest(true);
    try {
      await axios.post(
        `${API}/academy/sessions/${selectedSession.id}/add-guest`,
        {
          name: guestPlayerName,
          phone: guestPlayerPhone,
          email: guestPlayerEmail
        },
        { withCredentials: true }
      );

      toast.success(`Guest player "${guestPlayerName}" added to session`);
      setGuestPlayerName("");
      setGuestPlayerPhone("");
      setGuestPlayerEmail("");
      
      // Refresh sessions
      fetchSessions();
    } catch (error) {
      console.error("Error adding guest player:", error);
      toast.error(error.response?.data?.detail || "Failed to add guest player");
    } finally {
      setAddingGuest(false);
    }
  };

  // Cancel session and notify parents
  const cancelSession = async () => {
    if (!cancellingSession) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/academy/sessions/${cancellingSession.id}/cancel`,
        null,
        { 
          params: {
            reason: cancelReason || null,
            notify_whatsapp: cancelNotifyWhatsApp ? "true" : "false",
          },
          withCredentials: true 
        }
      );
      
      toast.success(response.data.message || "Session cancelled and parents notified");
      setShowCancelDialog(false);
      setCancellingSession(null);
      setCancelReason("");
      setCancelNotifyWhatsApp(false);
      fetchSessions();
    } catch (error) {
      console.error("Error cancelling session:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel session");
    } finally {
      setCancelling(false);
    }
  };

  // Broadcast message to all parents in a team
  const sendBroadcast = async () => {
    if (!broadcastTeamId || !broadcastMessage.trim()) {
      toast.error("Please select a team and enter a message");
      return;
    }
    
    setBroadcasting(true);
    try {
      const response = await axios.post(
        `${API}/academy/broadcast`,
        { team_id: broadcastTeamId, message: broadcastMessage },
        { withCredentials: true }
      );
      
      toast.success(
        `Message sent to ${response.data.sent} parent(s)` +
        (response.data.emailed ? ` · ${response.data.emailed} emailed` : "")
      );
      setShowBroadcastDialog(false);
      setBroadcastTeamId("");
      setBroadcastMessage("");
    } catch (error) {
      console.error("Error sending broadcast:", error);
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setBroadcasting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Age group for a session: the session's own label, else its team's
  const groupOf = (s) => {
    if (s.age_group) return s.age_group;
    const team = teams.find((t) => t.id === s.team_id);
    return team?.age_group || team?.name || null;
  };
  const sessionAgeGroups = Array.from(
    new Set(sessions.map(groupOf).filter(Boolean))
  ).sort();
  const matchesGroup = (s) => ageGroupFilter === "all" || groupOf(s) === ageGroupFilter;

  // Year -> term -> day, so a session is where you expect it and retrospective
  // marking is a click away rather than a toggle that resets.
  const activeYear = periods.find((y) => y.year === year);
  const activePeriod = (activeYear?.terms || []).find((t) => t.term_id === periodId);
  const dayOf = (s) => String(s.session_date || s.date || "").slice(0, 10);
  // Generated sessions carry no team, so name them by age group or programme
  // rather than showing "Unassigned".
  const sessionLabel = (s) => {
    if (s.team_name && s.team_name !== "Unassigned") return s.team_name;
    if (s.age_group) {
      return [s.age_group, s.gender && s.gender !== "mixed" ? s.gender : null]
        .filter(Boolean).join(" ");
    }
    const term = terms.find((t) => t.id === s.term_id);
    return term?.name || s.title || "Unassigned";
  };
  const visibleDays = Object.entries(
    sessions
      .filter((s) => s.status !== "archived" && matchesGroup(s))
      .filter((s) => {
        if (!activePeriod) return true;
        const day = dayOf(s);
        return day >= activePeriod.start && day <= activePeriod.end;
      })
      .reduce((acc, s) => {
        const day = dayOf(s);
        (acc[day] = acc[day] || []).push(s);
        return acc;
      }, {})
  ).sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <BackLink to={isCoach ? "/academy/coach-dashboard" : "/academy"} label="Back"
            className="text-white hover:text-blue-200"
            testId="sessions-back" />
          <h1 className="text-2xl font-bold text-white">Sessions &amp; Attendance</h1>
          <Link to="/academy/attendance" data-testid="sessions-to-attendance-link">
            <Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
              <Users className="w-4 h-4 mr-2" />
              Who attended
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-4xl font-bold text-white">Sessions</h2>
          {(isCoach || isAdmin) && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Session
            </Button>
          )}
          {(isCoach || isAdmin) && (
            <Button 
              onClick={() => setShowBroadcastDialog(true)} 
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Broadcast to Parents
            </Button>
          )}
        </div>

        {/* Filters: term selector + show past toggle */}
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="sessions-filter-bar">
          <div className="min-w-[220px]">
            <Select value={termFilter} onValueChange={setTermFilter}>
              <SelectTrigger data-testid="sessions-term-filter" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="All active terms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active terms</SelectItem>
                {terms.map((term) => (
                  <SelectItem key={term.id} value={term.id}>
                    {term.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <Select value={ageGroupFilter} onValueChange={setAgeGroupFilter}>
              <SelectTrigger data-testid="sessions-age-group-filter" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="All age groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All age groups</SelectItem>
                {sessionAgeGroups.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {termFilter !== "all" && (
            <span className="text-xs text-slate-400" data-testid="sessions-term-active-note">
              Showing all sessions for the selected term
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="sessions-year-tabs">
          {periods.map((y) => (
            <Button key={y.year} size="sm"
              variant={y.year === year ? "default" : "outline"}
              className={y.year === year
                ? "bg-yellow-500/100 text-black hover:bg-yellow-400"
                : "border-slate-600 text-slate-300 hover:bg-slate-800"}
              onClick={() => {
                setYear(y.year);
                localStorage.setItem("sessionsYear", y.year);
                const first = y.terms[0]?.term_id || null;
                setPeriodId(first);
                if (first) localStorage.setItem("sessionsTerm", first);
              }}
              data-testid={`sessions-year-${y.year}`}>
              {y.year}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="sessions-term-tabs">
          {(activeYear?.terms || []).map((t) => (
            <button key={t.term_id}
              onClick={() => {
                setPeriodId(t.term_id);
                localStorage.setItem("sessionsTerm", t.term_id);
              }}
              data-testid={`sessions-term-${t.term_id}`}
              className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                t.term_id === periodId
                  ? "border-yellow-500/100 bg-yellow-500/10"
                  : "border-white/10 bg-slate-900/50 hover:border-white/20"
              }`}>
              <span className="block text-sm font-semibold text-white">{t.term_name}</span>
              <span className="block text-xs text-slate-400">
                {t.days} training days · {t.marked} of {t.sessions} registers done
              </span>
            </button>
          ))}
        </div>

        <div className="space-y-10">
          {visibleDays.length === 0 ? (
            <Card className="p-8 text-center" data-testid="sessions-empty">
              <p className="text-slate-400">No sessions in this term for that age group.</p>
            </Card>
          ) : visibleDays.map(([day, daySessions]) => (
            <div key={day} data-testid={`sessions-day-${day}`}>
              <h3 className="text-lg font-bold text-white mb-4">
                {new Date(`${day}T00:00:00`).toLocaleDateString("en-SG", {
                  weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  {daySessions.length} session{daySessions.length === 1 ? "" : "s"}
                </span>
              </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {daySessions.map((session) => (
                <Card key={session.id} className="p-6 bg-slate-900/50 border-2 border-blue-500/30 hover:border-blue-500/30 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-white">{sessionLabel(session)}</h4>
                      <p className="text-sm text-slate-400 capitalize">{session.session_type}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      session.session_type === 'game' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {session.session_type === 'game' ? 'Game' : 'Training'}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-slate-400">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(session.session_date).toLocaleDateString()}
                      {session.start_time && session.end_time && (
                        <span className="ml-2 font-semibold">
                          {session.start_time} - {session.end_time}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-slate-400">
                      <MapPin className="w-4 h-4 mr-2" />
                      {session.location}
                    </div>
                    {session.opponent && (
                      <div className="text-sm text-slate-400">
                        <strong>vs</strong> {session.opponent}
                      </div>
                    )}
                    <div className="flex items-center text-sm text-slate-400">
                      <Users className="w-4 h-4 mr-2" />
                      {session.signups?.length || 0} signed up
                      {session.squad_limit && ` / ${session.squad_limit} limit`}
                    </div>
                  </div>

                  {isPlayer && (
                    <Button 
                      onClick={() => signupForSession(session.id)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      size="sm"
                      disabled={session.signups?.includes(user.academy_player_id)}
                    >
                      {session.signups?.includes(user.academy_player_id) ? 'Signed Up' : 'Sign Up'}
                    </Button>
                  )}

                  {(isCoach || isAdmin) && (
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        size="sm"
                        onClick={() => openEditDialog(session)}
                      >
                        ✏️ Edit Session
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        size="sm"
                        onClick={() => openManagePlayers(session)}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Manage Players
                      </Button>
                      <Link to={`/academy/attendance/${session.id}`}>
                        <Button variant="outline" className="w-full" size="sm">
                          Mark Attendance
                        </Button>
                      </Link>
                      {!session.match_report_submitted ? (
                        <Button 
                          variant="outline" 
                          className={`w-full ${
                            session.attendance_taken 
                              ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' 
                              : 'border-slate-700 text-slate-400 cursor-not-allowed'
                          }`}
                          size="sm"
                          onClick={() => {
                            if (!session.attendance_taken) {
                              toast.error("Please mark attendance first before submitting a match report", {
                                duration: 4000
                              });
                              return;
                            }
                            navigate(`/academy/sessions/${session.id}/match-report`);
                          }}
                        >
                          Submit Match Report {!session.attendance_taken && '(Attendance Required)'}
                        </Button>
                      ) : (
                        <Link to={`/academy/sessions/${session.id}/match-report/view`}>
                          <Button 
                            variant="outline" 
                            className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10" 
                            size="sm"
                          >
                            View Report
                          </Button>
                        </Link>
                      )}
                      {/* Cancel Session Button */}
                      <Button 
                        variant="outline" 
                        className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10" 
                        size="sm"
                        onClick={() => {
                          setCancellingSession(session);
                          setShowCancelDialog(true);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Session
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          className="w-full text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                          size="sm"
                          data-testid={`delete-session-${session.id}`}
                          onClick={() => deleteSession(session)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete (created by mistake)
                        </Button>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Session Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Training/Game Session</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={createSession} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-300">Team *</label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-300">Session Type *</label>
              <div
                className="mt-1 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-300"
                data-testid="session-type-training-only"
              >
                Training
              </div>
              <p className="mt-2 text-xs text-slate-400" data-testid="games-live-in-fixtures-note">
                Every game — league or friendly — now lives on the{" "}
                <Link to="/academy/fixtures" className="underline font-semibold text-amber-300">
                  League Games
                </Link>{" "}
                page, so there is one place to look. Add it there and parents are WhatsApped for a
                yes/no straight away.
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-300">Date *</label>
              <Input 
                type="date" 
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-300">Location *</label>
              <Input 
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Dempsey"
                required
              />
            </div>

            {sessionType === 'training' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-300">Start Time *</label>
                  <Input 
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">End Time *</label>
                  <Input 
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            {sessionType === 'game' && (
              <>
                <div>
                  <label className="text-sm font-semibold text-slate-300">Opponent</label>
                  <Input 
                    value={opponent}
                    onChange={(e) => setOpponent(e.target.value)}
                    placeholder="Opponent team name"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">Reporting Time</label>
                  <Input 
                    type="time"
                    value={reportingTime}
                    onChange={(e) => setReportingTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">Game Time</label>
                  <Input 
                    type="time"
                    value={gameTime}
                    onChange={(e) => setGameTime(e.target.value)}
                  />
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-semibold text-slate-300">Squad Limit (optional)</label>
              <Input 
                type="number"
                value={squadLimit}
                onChange={(e) => setSquadLimit(e.target.value)}
                placeholder="Leave empty for no limit"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-blue-600 hover:bg-blue-700">
                {creating ? "Creating..." : "Create Session"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Players Dialog */}
      <Dialog open={showManagePlayersDialog} onOpenChange={setShowManagePlayersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Session Players</DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setManagePlayersTab("registered")}
              className={`px-4 py-2 font-semibold transition-colors ${
                managePlayersTab === "registered"
                  ? "border-b-2 border-blue-600 text-blue-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
            >
              Team Players
            </button>
            <button
              onClick={() => setManagePlayersTab("guest")}
              className={`px-4 py-2 font-semibold transition-colors ${
                managePlayersTab === "guest"
                  ? "border-b-2 border-blue-600 text-blue-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
            >
              + Add Guest
            </button>
          </div>
          
          {selectedSession && (
            <div className="space-y-4">
              <div className="bg-slate-800/50 p-4 rounded-lg">
                <h3 className="font-semibold text-white">{selectedSession.team_name}</h3>
                <p className="text-sm text-slate-400">
                  {new Date(selectedSession.session_date).toLocaleDateString()}
                  {selectedSession.start_time && ` at ${selectedSession.start_time}`}
                </p>
                <p className="text-sm text-slate-400">
                  {selectedSession.signups?.length || 0} player(s) signed up
                  {selectedSession.squad_limit && ` / ${selectedSession.squad_limit} limit`}
                </p>
              </div>

              {managePlayersTab === "registered" && (
                <>
                  {loadingPlayers ? (
                    <div className="text-center py-4">Loading players...</div>
                  ) : (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-white">Team Players</h4>
                      {teamPlayers.length === 0 ? (
                        <p className="text-sm text-slate-400">No players in this team</p>
                      ) : (
                        <div className="space-y-2">
                          {teamPlayers.map((player) => {
                            const isSignedUp = selectedSession.signups?.includes(player.user_id);
                            return (
                              <div 
                                key={player.user_id || player.id} 
                                className="flex items-center justify-between p-3 bg-slate-900/50 border rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-white">{player.name}</p>
                                  <p className="text-sm text-slate-400">
                                    {player.position || 'No position'}
                                  </p>
                                </div>
                                {isSignedUp ? (
                                  <span className="text-sm text-emerald-400 font-semibold">
                                    ✓ Signed Up
                                  </span>
                                ) : (
                                  <Button 
                                    size="sm"
                                    onClick={() => addPlayerToSession(player.user_id || player.id)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    Add to Session
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {managePlayersTab === "guest" && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-400">
                    Add a guest player (non-registered person) to the session
                  </p>

                  <div className="space-y-4 bg-slate-900/50 border rounded p-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-300 mb-1 block">
                        Guest Name *
                      </label>
                      <Input
                        value={guestPlayerName}
                        onChange={(e) => setGuestPlayerName(e.target.value)}
                        placeholder="Enter guest player name"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-slate-300 mb-1 block">
                        Phone (Optional)
                      </label>
                      <Input
                        value={guestPlayerPhone}
                        onChange={(e) => setGuestPlayerPhone(e.target.value)}
                        placeholder="+65 1234 5678"
                        type="tel"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-slate-300 mb-1 block">
                        Email (Optional)
                      </label>
                      <Input
                        value={guestPlayerEmail}
                        onChange={(e) => setGuestPlayerEmail(e.target.value)}
                        placeholder="guest@example.com"
                        type="email"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={addGuestPlayerToSession}
                    disabled={!guestPlayerName.trim() || addingGuest}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50"
                  >
                    {addingGuest ? "Adding..." : "Add Guest Player"}
                  </Button>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowManagePlayersDialog(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Session</DialogTitle>
          </DialogHeader>
          
          {editingSession && (
            <form onSubmit={updateSession} className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-semibold text-slate-300">Date *</label>
                <Input 
                  type="date" 
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-300">Start Time *</label>
                  <Input 
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">End Time *</label>
                  <Input 
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-300">Location *</label>
                <Input 
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                  placeholder="e.g., Dempsey"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-300">Squad Limit (Optional)</label>
                <Input 
                  type="number"
                  value={editSquadLimit}
                  onChange={(e) => setEditSquadLimit(e.target.value)}
                  placeholder="Leave empty for no limit"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={updating}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {updating ? "Updating..." : "Update Session"}
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Session Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              Cancel Session
            </DialogTitle>
            <DialogDescription>
              Cancel this training session and optionally notify enrolled parents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {cancellingSession && (
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="font-semibold text-white">{cancellingSession.team_name}</p>
                <p className="text-sm text-slate-400">
                  {new Date(cancellingSession.session_date).toLocaleDateString()} at {cancellingSession.start_time}
                </p>
                <p className="text-sm text-slate-400">{cancellingSession.location}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-semibold text-slate-300">Reason for Cancellation (optional)</label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Heavy rain expected, Field maintenance..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-400">
                <Send className="w-4 h-4 inline mr-2" />
                All enrolled parents will be notified via Telegram
              </p>
            </div>

            <label className="flex items-start gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg cursor-pointer" data-testid="cancel-notify-whatsapp-label">
              <input
                type="checkbox"
                checked={cancelNotifyWhatsApp}
                onChange={(e) => setCancelNotifyWhatsApp(e.target.checked)}
                className="mt-1 h-4 w-4 accent-emerald-500"
                data-testid="cancel-notify-whatsapp"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-300">Also send a WhatsApp blast to enrolled parents</p>
                <p className="text-xs text-emerald-200/80 mt-0.5">
                  Uses your saved WhatsApp settings. Skipped automatically if WhatsApp is disabled. Opt-outs are respected.
                </p>
              </div>
            </label>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancellingSession(null);
                  setCancelReason("");
                  setCancelNotifyWhatsApp(false);
                }}
              >
                Keep Session
              </Button>
              <Button
                onClick={cancelSession}
                disabled={cancelling}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? "Cancelling..." : "Cancel & Notify"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast to Parents Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-400">
              <MessageSquare className="w-5 h-5" />
              Broadcast to Parents
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-300">Select Age Group / Team *</label>
              <Select value={broadcastTeamId} onValueChange={setBroadcastTeamId}>
                <SelectTrigger className="mt-1" data-testid="broadcast-team-select">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {broadcastTeams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}{team.age_group ? ` · ${team.age_group}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1" data-testid="broadcast-scope-note">
                {broadcastScope === "all"
                  ? `You can message any squad (${broadcastTeams.length} available)`
                  : `Your squads only (${broadcastTeams.length})`}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-300">Message *</label>
              <Textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Type your message to parents..."
                className="mt-1"
                rows={4}
              />
              <p className="text-xs text-slate-500 mt-1">
                {broadcastMessage.length}/500 characters
              </p>
            </div>
            
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-sm text-purple-400">
                <Send className="w-4 h-4 inline mr-2" />
                Parents get this on their dashboard and by email, plus Telegram where it's linked
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowBroadcastDialog(false);
                  setBroadcastTeamId("");
                  setBroadcastMessage("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendBroadcast}
                disabled={broadcasting || !broadcastTeamId || !broadcastMessage.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {broadcasting ? "Sending..." : "Send Broadcast"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
